import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore, API_BASE, apiFetch } from '../store/auth';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  DateRangePicker,
  Pagination,
  formatDate,
  formatFileSize,
} from '../components';

interface BankStatement {
  id: string;
  bankType: string;
  accountId: string | null;
  statementDate: string;
  periodStart: string;
  periodEnd: string;
  fileSize: number | null;
  transactionCount: number;
  status: string;
  createdAt: string;
}

interface StatementSummary {
  totalStatements: number;
  totalSize: number;
  byBank: Record<string, number>;
}

const BANK_COLORS: Record<string, string> = {
  holvi: 'bg-orange-100 text-orange-800',
  op: 'bg-blue-100 text-blue-800',
  nordea: 'bg-red-100 text-red-800',
  sumup: 'bg-purple-100 text-purple-800',
};

const BANK_LABELS: Record<string, string> = {
  holvi: 'Holvi',
  op: 'OP',
  nordea: 'Nordea',
  sumup: 'SumUp',
};

export default function BankStatementsPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [summary, setSummary] = useState<StatementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    bankType: '',
    startDate: '',
    endDate: '',
  });
  const limit = 25;

  const fetchStatements = useCallback(async () => {
    if (!activeOrg) return;

    setLoading(true);
    setError('');

    try {
      const offset = (currentPage - 1) * limit;
      const params = new URLSearchParams();
      params.append('organization_id', activeOrg.id);
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      if (filters.bankType) params.append('bank_type', filters.bankType);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const response = await apiFetch(`${API_BASE}/bank-statements?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();

      setStatements(data.statements);
      setTotalCount(data.pagination.total);
    } catch (err) {
      setError('Tiliotteiden haku epäonnistui');
      console.error('Failed to fetch statements:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, currentPage, filters]);

  const fetchSummary = useCallback(async () => {
    if (!activeOrg) return;

    try {
      const response = await apiFetch(`${API_BASE}/bank-statements/summary?organization_id=${activeOrg.id}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [activeOrg]);

  const downloadStatement = async (statementId: string, bankType: string) => {
    if (!activeOrg) return;

    try {
      const response = await apiFetch(
        `${API_BASE}/bank-statements/${statementId}/download?organization_id=${activeOrg.id}`
      );
      if (!response.ok) throw new Error('Failed to download');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement-${bankType}-${statementId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Lataus epäonnistui');
      console.error('Failed to download statement:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeOrg) return;

    // Default period: previous month
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    const periodStart = prevMonth.toISOString().split('T')[0];
    const periodEnd = lastDayOfPrevMonth.toISOString().split('T')[0];

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams();
      params.append('organization_id', activeOrg.id);
      params.append('bank_type', filters.bankType || 'manual');
      params.append('period_start', periodStart);
      params.append('period_end', periodEnd);

      const response = await apiFetch(`${API_BASE}/bank-statements/upload?${params.toString()}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      alert('Tiliote ladattu onnistuneesti!');
      fetchStatements();
      fetchSummary();
    } catch (err) {
      setError('Lataus epäonnistui: ' + (err instanceof Error ? err.message : 'Tuntematon virhe'));
      console.error('Failed to upload statement:', err);
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  useEffect(() => {
    fetchStatements();
    fetchSummary();
  }, [fetchStatements, fetchSummary]);

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Tiliotteet" />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Arkistoituja tiliotteita">
            <div className="text-3xl font-bold text-indigo-600">{summary.totalStatements}</div>
            <div className="text-sm text-gray-500">kpl</div>
          </Card>
          <Card title="Kokonaiskoko">
            <div className="text-3xl font-bold text-indigo-600">{formatFileSize(summary.totalSize)}</div>
            <div className="text-sm text-gray-500">R2-tallennustila</div>
          </Card>
          <Card title="Pankkien mukaan">
            <div className="space-y-1">
              {Object.entries(summary.byBank).map(([bank, count]) => (
                <div key={bank} className="flex justify-between text-sm">
                  <span>{BANK_LABELS[bank] || bank}:</span>
                  <span className="font-medium">{count} kpl</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Upload */}
      <Card title="Suodattimet ja lataus">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Pankki"
            value={filters.bankType}
            onChange={(e) => setFilters(f => ({ ...f, bankType: e.target.value }))}
            options={[
              { value: '', label: 'Kaikki pankit' },
              { value: 'holvi', label: 'Holvi' },
              { value: 'op', label: 'OP' },
              { value: 'nordea', label: 'Nordea' },
              { value: 'sumup', label: 'SumUp' },
              { value: 'manual', label: 'Manuaalinen' },
            ]}
          />
          <DateRangePicker
            startLabel="Alkaen"
            endLabel="Päättyen"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(v) => setFilters(f => ({ ...f, startDate: v }))}
            onEndDateChange={(v) => setFilters(f => ({ ...f, endDate: v }))}
          />
          <div className="flex items-end gap-2">
            <Button variant="secondary" onClick={fetchStatements}>
              🔄 Päivitä
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button variant="primary" disabled={uploading}>
                {uploading ? '⏳ ' + t('common.uploading') : '📄 ' + t('bankStatements.downloadPdf')}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Statements Table */}
      <Card title={`Tiliotteet (${totalCount})`}>
        <DataTable>
          <Table
            columns={[
              {
                key: 'statementDate',
                header: 'Päivämäärä',
                width: '120px',
                render: (s: BankStatement) => formatDate(s.statementDate),
              },
              {
                key: 'bankType',
                header: 'Pankki',
                width: '100px',
                render: (s: BankStatement) => (
                  <Badge className={BANK_COLORS[s.bankType] || 'bg-gray-100'}>
                    {BANK_LABELS[s.bankType] || s.bankType}
                  </Badge>
                ),
              },
              {
                key: 'period',
                header: 'Kausi',
                render: (s: BankStatement) => (
                  <span className="text-sm">
                    {formatDate(s.periodStart)} - {formatDate(s.periodEnd)}
                  </span>
                ),
              },
              {
                key: 'size',
                header: 'Koko',
                width: '100px',
                align: 'right',
                render: (s: BankStatement) => s.fileSize ? formatFileSize(s.fileSize) : '—',
              },
              {
                key: 'transactions',
                header: 'Tapahtumat',
                width: '120px',
                align: 'right',
                render: (s: BankStatement) => s.transactionCount > 0 ? `${s.transactionCount} kpl` : '—',
              },
              {
                key: 'actions',
                header: '',
                width: '100px',
                align: 'right',
                render: (s: BankStatement) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadStatement(s.id, s.bankType)}
                  >
                    ⬇️ Lataa
                  </Button>
                ),
              },
            ]}
            data={statements}
            keyExtractor={(s) => s.id}
            loading={loading}
            emptyMessage="Ei arkistoituja tiliotteita"
          />
        </DataTable>

        {totalCount > limit && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / limit)}
              onPageChange={setCurrentPage}
              totalItems={totalCount}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
